import {
	Engine,
	Scene,
	Vector3,
	HemisphericLight,
	DirectionalLight,
	PointLight,
	UniversalCamera,
	MeshBuilder,
	StandardMaterial,
	Color3,
	Color4,
	DefaultRenderingPipeline,
	ImageProcessingConfiguration,
	WebXRDefaultExperience,
	WebXRFeatureName,
	WebXRState,
	VirtualJoystick,
} from "@babylonjs/core";
import type { UtahRegion } from "@utah-vr/shared";
import { attachUtahTiles } from "./world/tiles";
import { buildWarehouse, type WarehouseHandles } from "./world/warehouse";
import { buildOutsideWorld, type InteractZone } from "./world/buildings";
import { spawnNpcCrowd, type NpcCrowd } from "./world/npcs";
import { createWeather, type Weather } from "./world/weather";
import { createWorkerVisuals, type WorkerVisualsManager } from "./world/workers";

export interface SceneHandles {
	engine: Engine;
	scene: Scene;
	warehouse: WarehouseHandles;
	cam: UniversalCamera;
	interactZones: InteractZone[];
	npcs: NpcCrowd;
	weather: Weather;
	workerVisuals: WorkerVisualsManager;
	enterXR: () => Promise<void>;
}

function attachTouchControls(scene: Scene, cam: UniversalCamera) {
	const moveStick = new VirtualJoystick(true);
	const lookStick = new VirtualJoystick(false);
	const vjCanvas = VirtualJoystick.Canvas!;
	vjCanvas.style.zIndex = "2";
	vjCanvas.style.touchAction = "none";
	(vjCanvas.style as CSSStyleDeclaration & { webkitTouchCallout?: string; webkitUserSelect?: string }).webkitTouchCallout = "none";
	(vjCanvas.style as CSSStyleDeclaration & { webkitTouchCallout?: string; webkitUserSelect?: string }).webkitUserSelect = "none";
	moveStick.setJoystickColor("#7df");
	lookStick.setJoystickColor("#fc7");

	const forward = new Vector3();
	const right = new Vector3();
	scene.onBeforeRenderObservable.add(() => {
		const dt = scene.getEngine().getDeltaTime() / 1000;
		if (moveStick.pressed) {
			cam.getDirectionToRef(Vector3.Forward(), forward);
			forward.y = 0;
			forward.normalize();
			cam.getDirectionToRef(Vector3.Right(), right);
			right.y = 0;
			right.normalize();
			const speed = 3.0 * dt;
			const dx = (moveStick.deltaPosition.x ?? 0) * speed;
			const dz = (moveStick.deltaPosition.y ?? 0) * speed;
			const delta = forward.scale(dz).add(right.scale(dx));
			cam.cameraDirection.addInPlace(delta);
		}
		if (lookStick.pressed) {
			const sensitivity = 0.04;
			cam.cameraRotation.y += (lookStick.deltaPosition.x ?? 0) * sensitivity;
			cam.cameraRotation.x -= (lookStick.deltaPosition.y ?? 0) * sensitivity;
		}
	});
}

export async function createScene(canvas: HTMLCanvasElement, region: UtahRegion): Promise<SceneHandles> {
	const engine = new Engine(canvas, true, { stencil: true, preserveDrawingBuffer: false });
	const scene = new Scene(engine);
	scene.clearColor.set(0.55, 0.7, 0.85, 1);
	scene.collisionsEnabled = true;
	scene.ambientColor = new Color3(0.05, 0.05, 0.08);

	const cam = new UniversalCamera("player", new Vector3(0, 1.7, -10), scene);
	cam.setTarget(new Vector3(0, 1.6, 0));
	cam.attachControl(canvas, true);
	cam.speed = 0.5;
	cam.minZ = 0.05;
	cam.keysUp = [87];
	cam.keysDown = [83];
	cam.keysLeft = [65];
	cam.keysRight = [68];
	cam.checkCollisions = true;
	cam.ellipsoid = new Vector3(0.4, 0.9, 0.4);

	const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
	if (isTouch) attachTouchControls(scene, cam);

	const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), scene);
	hemi.intensity = 0.7;
	hemi.groundColor = new Color3(0.25, 0.22, 0.2);
	const sun = new DirectionalLight("sun", new Vector3(-0.5, -1, -0.3), scene);
	sun.intensity = 2.2; // PBR needs more lumens than StandardMaterial
	sun.diffuse = new Color3(1.0, 0.95, 0.86);
	const interior = new PointLight("interior", new Vector3(0, 4.5, 8), scene);
	interior.intensity = 0.8;
	interior.range = 22;
	interior.diffuse = new Color3(1, 0.9, 0.7); // warm interior tone

	// Default rendering pipeline: bloom + ACES tonemapping + sharper anti-alias.
	const pipeline = new DefaultRenderingPipeline("utah-pipeline", true, scene, [cam]);
	pipeline.samples = 4;
	pipeline.fxaaEnabled = true;
	pipeline.bloomEnabled = true;
	pipeline.bloomThreshold = 0.85;
	pipeline.bloomWeight = 0.25;
	pipeline.bloomScale = 0.5;
	pipeline.imageProcessing.toneMappingEnabled = true;
	pipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
	pipeline.imageProcessing.exposure = 1.0;
	pipeline.imageProcessing.contrast = 1.15;
	pipeline.imageProcessing.vignetteEnabled = true;
	pipeline.imageProcessing.vignetteWeight = 1.5;
	pipeline.imageProcessing.vignetteColor = new Color4(0, 0, 0, 0);

	// Day-night cycle: full cycle every 6 minutes of real time.
	const CYCLE_SEC = 360;
	scene.onBeforeRenderObservable.add(() => {
		const dt = scene.getEngine().getDeltaTime() / 1000;
		(sun as { _tCycle?: number })._tCycle = ((sun as { _tCycle?: number })._tCycle ?? 0) + dt;
		const phase = (((sun as { _tCycle?: number })._tCycle ?? 0) % CYCLE_SEC) / CYCLE_SEC;
		const angle = phase * Math.PI * 2;
		const sx = Math.cos(angle);
		const sy = Math.sin(angle);
		sun.direction.set(-sx, -Math.max(0.05, sy), -0.3).normalize();
		const day = Math.max(0, sy);
		sun.intensity = 0.15 + day * 0.85;
		hemi.intensity = 0.25 + day * 0.4;
		// Sky color: dawn orange → blue → sunset orange → night navy
		const dawn = Math.max(0, Math.sin(angle * 0.5) * (1 - Math.abs(Math.cos(angle))));
		const r = 0.06 + day * 0.5 + dawn * 0.4;
		const g = 0.06 + day * 0.65;
		const b = 0.18 + day * 0.7;
		scene.clearColor.set(Math.min(1, r), Math.min(1, g), Math.min(1, b), 1);
		interior.intensity = 0.45 + (1 - day) * 0.55; // brighter at night
	});

	const groundMat = new StandardMaterial("ground", scene);
	groundMat.diffuseColor = new Color3(0.32, 0.34, 0.36);
	const ground = MeshBuilder.CreateGround("ground", { width: 200, height: 200 }, scene);
	ground.material = groundMat;
	ground.checkCollisions = true;

	const warehouse = buildWarehouse(scene);
	const { zones: interactZones } = buildOutsideWorld(scene);
	const npcs = spawnNpcCrowd(scene, 14);
	const workerVisuals = createWorkerVisuals(scene, warehouse.root);

	// Anchor mesh for weather (rain/snow follows the camera so particles stay near the player)
	const weatherAnchor = MeshBuilder.CreateBox("weather-anchor", { size: 0.01 }, scene);
	weatherAnchor.isVisible = false;
	const weather = createWeather(scene, weatherAnchor);

	// Tap-to-pick mesh handler: callers register via window-level event.
	scene.onPointerDown = (_evt, pickInfo) => {
		const id = pickInfo.pickedMesh?.metadata?.printerId
			?? (pickInfo.pickedMesh?.parent as { metadata?: { printerId?: string } } | null)?.metadata?.printerId;
		if (id) {
			window.dispatchEvent(new CustomEvent("printer-tap", { detail: { id } }));
		}
	};

	await attachUtahTiles(scene, region).catch((e) => console.warn("Tiles disabled:", e));

	const xrStatus = document.getElementById("xr-status");
	let enterXR = async () => {};

	if ("xr" in navigator) {
		try {
			const xr = await WebXRDefaultExperience.CreateAsync(scene, {
				floorMeshes: [ground],
				optionalFeatures: true,
				// Disable teleportation so the thumbstick can drive smooth
				// locomotion via the MOVEMENT feature instead.
				disableTeleportation: true,
			});

			try {
				xr.baseExperience.featuresManager.enableFeature(
					WebXRFeatureName.MOVEMENT,
					"latest",
					{
						xrInput: xr.input,
						movementSpeed: 0.12,
						rotationSpeed: 0.25,
						movementOrientationFollowsViewerPose: true,
					},
					true,
					false,
				);
			} catch (err) {
				console.warn("XR movement feature unavailable:", err);
			}

			xr.baseExperience.onStateChangedObservable.add((state) => {
				if (!xrStatus) return;
				if (state === WebXRState.IN_XR) xrStatus.textContent = "XR: in immersive session (thumbstick to walk)";
				else if (state === WebXRState.NOT_IN_XR) xrStatus.textContent = "XR: ready (click VR button)";
			});
			enterXR = async () => {
				const supported = await xr.baseExperience.sessionManager.isSessionSupportedAsync("immersive-vr");
				if (xrStatus) xrStatus.textContent = supported ? "XR: ready (click VR button bottom-right)" : "XR: device not detected";
			};
		} catch (err) {
			console.warn("WebXR setup failed:", err);
			if (xrStatus) xrStatus.textContent = "XR: setup failed (continuing in flat mode)";
		}
	} else if (xrStatus) {
		xrStatus.textContent = "XR: not supported on this browser (iOS Safari — flat mode)";
	}

	return { engine, scene, warehouse, cam, interactZones, npcs, weather, workerVisuals, enterXR };
}
